import React, { useState, useEffect, useCallback, useMemo } from "react";
import { showSpinner, closeSpinner, modal, alert } from "../modules/Dialogs";
import { sessionExpired } from "../config/GeneralConfig";
import { toScreen } from "../modules/Routing";
import PropTypes from "prop-types";
import { useNavigate } from "react-router-dom";
import moment from "moment-timezone";
import Pagination from "./Pagination";
import { getretrieveTransfers, getretrieveTransfersDetails } from "../modules/Transfers";
import { Tooltip } from "react-tooltip";
import { Box, Button, Drawer } from "@mui/material";
import TransfersDetails from "./TransfersDetails";
import {
  getSafariCompatibleDrawerSx,
  getSafariCompatibleBoxSx,
} from "../modules/DrawerUtils";

// Main component
const Transfers = ({ userDetails }) => {
  const navigate = useNavigate();
  const jwtToken = userDetails.jwttoken;

  const [isRightDrawerOpen, setIsRightDrawerOpen] = useState(false);
  const [searchValue, setSearchValue] = useState(null);
  const [search, setSearch] = useState("");
  const [pageSize, setPageSize] = useState(15);
  const [totalPages, setTotalPages] = useState(1);
  const [page, setPage] = useState(1);
  const [inputValue, setInputValue] = useState(1);
  const [transfers, setTransfers] = useState([]);
  const [sort, setSort] = useState("");
  const [currentView, setCurrentView] = useState(null);
  const [selectedTransfer, setSelectedTransfer] = useState(null);
  const [transferTypeFilter, setTransferTypeFilter] = useState("all");
  const [transferDetails, setTransferDetails] = useState([]);


  const toggleDrawer = useCallback(
    (anchor, open, view = null, transfer = null) =>
      (event) => {
        if (
          event?.type === "keydown" &&
          (event?.key === "Tab" || event?.key === "Shift")
        ) {
          return;
        }

        setIsRightDrawerOpen(open);

        if (open) {
          setCurrentView(view);
          if (transfer) {
            setSelectedTransfer(transfer || null);
          }
        }

        // updateState(updates); // This line is removed as per the edit hint
      },
    [setIsRightDrawerOpen, setCurrentView, setSelectedTransfer]
  );

  const infoIcon = (
    <svg
      xmlns="http://www.w3.org/2000/svg"
      width="14"
      height="14"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden="true"
    >
      <circle cx="12" cy="12" r="10"></circle>
      <path d="M12 16v-4"></path>
      <path d="M12 8h.01"></path>
    </svg>
  );

  const STATUS_BADGE_MAP = {
    pending: { class: "badge-warning", label: "Pending" },
    complete: { class: "badge-success", label: "Completed" },
    rejected: { class: "badge-danger", label: "Rejected" },
    "in review": { class: "badge-info", label: "In Review", icon1: infoIcon },
    default: { class: "badge-secondary", label: "-" },
    "not applicable": { class: "badge-secondary", label: "Not applicable" },
  };

  const getStatusBadge = (status) =>
    STATUS_BADGE_MAP[status] || STATUS_BADGE_MAP?.default;

  const renderTransferRow = (transfer, index) => {
    const statusBadge = getStatusBadge(
      transfer?.jobDetails?.mavPaymentDetails?.paymentTransfer
    );

    const calculateDriverPay = (transfer) => {
      const clientPaid = transfer?.jobDetails?.originalPrice / 100 || 0;
      const commission =
        ((transfer?.jobDetails?.originalPrice /
          (1 + transfer?.jobDetails?.commission)) *
          transfer?.jobDetails?.commission) /
        100 || 0;
      const driverPay = clientPaid - commission;
      return driverPay.toFixed(2);
    };

    return (
      <div className="c-list-entry align-items-center" key={index}>
        {/* Driver Info */}
        <div className="c-list-entry__mavname">
          <b className="text-capitalize">
            {transfer?.driverDetails?.firstname ||
              transfer?.driverDetails?.lastname
              ? `${transfer.driverDetails.firstname} ${transfer.driverDetails.lastname}`
              : "-"}
          </b>
          <br />
          <span
            className="text-sm u-color-teal u-cursor-pointer"
            onClick={() => navigate(`/mav/${transfer?.driverDetails?.shortId}`)}
          >
            {transfer?.driverDetails?.shortId}
          </span>
        </div>

        {/* Status Badge */}
        <div className="c-list-entry__rating">
          <span className={`badge text-capitalize ${statusBadge.class}`}>
            {statusBadge.label}
            {statusBadge.icon1 && (
              <>
                <span
                  className="tooltipStatus cursor-help"
                  data-tooltip-id={`tooltip-transfer-${transfer?.jobDetails?._id}`}
                >
                  {statusBadge.icon1}
                </span>
                <Tooltip
                  id={`tooltip-transfer-${transfer?.jobDetails?._id}`}
                  className="custom-tooltip"
                  place="top"
                >
                  Customer Refund decision Pending
                </Tooltip>
              </>
            )}
          </span>
        </div>

        {/* Driver Pay */}
        <div className="c-list-entry__mavquotes">
          <b> £{calculateDriverPay(transfer) || "0"}</b>
        </div>

        {/* Job Type */}
        <div className="c-list-entry__mavjobs text-capitalize">
          {transfer?.jobDetails?.jobType}
        </div>

        {/* Location */}
        <div className="c-list-entry__location">
          <span className="text-capitalize">
            <div>{transfer?.jobDetails?.from || ""}</div>
            {(transfer?.jobDetails?.from || transfer?.jobDetails?.to) && (
              <div className="text-sm text-gray-600">
                {transfer?.jobDetails?.to ? "→ " : ""}
                {transfer?.jobDetails?.to || ""}
              </div>
            )}
          </span>
        </div>
        {/* Created Date/Time */}
        <div className="c-list-entry__refunds">
          <span className="u-before-icon-calendar2">
            &nbsp;&nbsp;
            {transfer?.jobDetails?.mavPaymentDetails?.transferCompletedOn
              ? moment(transfer?.jobDetails?.mavPaymentDetails?.transferCompletedOn)
                ?.tz("Europe/London")?.format("DD MMM YYYY")
              : "-"}
          </span>
          <br />
          <span className="u-before-icon-clock">
            &nbsp;&nbsp;
            {transfer?.jobDetails?.mavPaymentDetails?.transferCompletedOn
              ? moment(transfer?.jobDetails?.mavPaymentDetails?.transferCompletedOn)
                ?.tz("Europe/London")?.format("hh:mm ")
              : "-"}
          </span>

        </div>

        {/* Drawer with View Button */}
        <div className="c-list-entry__uses">
          <Button
            className="btn btn-sm btn-primary"
            onClick={toggleDrawer("right", true, "edit", (e) => getTransfersDetails(e, transfer?.jobDetails?._id))}
          >
            View
          </Button>
          <Drawer
            anchor="right"
            open={isRightDrawerOpen}
            onClose={toggleDrawer("right", false)}
            ModalProps={{
              BackdropProps: {
                style: { backgroundColor: "rgba(0, 0, 0, 0.1)" },
              },
            }}
            sx={getSafariCompatibleDrawerSx(600)}
          >
            {transfersPropsPage("right")}
          </Drawer>
        </div>
      </div>
    );
  };

  const ColumnHeaders = React.memo(({ sort, onSort }) => (
    <div className="c-list-entry c-list-entry__header">
      <div className="c-list-entry__mavname c-list-filter">
        <SortButton field="firstname" label="Driver" />
      </div>
      <div className="c-list-entry__rating c-list-filter">
        <SortButton field="statusSorter" label="Transfer" />
      </div>
      <div className="c-list-entry__mavquotes c-list-filter">
        <SortButton field="amountSorter" label="Driver Pay" />
      </div>
      <div className="c-list-entry__mavjobs c-list-filter">
        {/* <SortButton  label="Job Type" /> */}
        Job Type
      </div>
      <div className="c-list-entry__location c-list-filter">
        {/* <SortButton label="Location" /> */}
        Location
      </div>
      <div className="c-list-entry__refunds c-list-filter">
        <SortButton field="completedOnSorter" label="Completed On" />
      </div>
      <div className="c-list-entry__uses c-list-filter">
        <button className="c-list-filter__button--faded">Actions</button>
      </div>
    </div>
  ));

  const sortTransfers = (event, field) => {
    if (event) event.preventDefault();

    const sortFields = {
      firstname: { asc: "firstname=1", desc: "firstname=-1" },
      amountSorter: { asc: "amountSorter=1", desc: "amountSorter=-1" },
      statusSorter: { asc: "statusSorter=1", desc: "statusSorter=-1" },
      completedOnSorter: {
        asc: "completedOnSorter=1",
        desc: "completedOnSorter=-1",
      },
    };

    const currentSort = sortFields[field];
    if (!currentSort) return;

    const newSort =
      sort === currentSort.asc ? currentSort.desc : currentSort.asc;

    setSearch("");
    setSort(newSort);
    setPage(1);
  };

  const getSortClass = (key) => {
    if (sort === `${key}=1`) {
      return "c-list-filter__button c-list-filter__button--icon-up";
    } else if (sort === `${key}=-1`) {
      return "c-list-filter__button c-list-filter__button--icon-down";
    }
    return "c-list-filter__button c-list-filter__button--faded";
  };

  const SortButton = useCallback(
    ({ field, label }) => (
      <button
        className={getSortClass(field)}
        onClick={(e) => sortTransfers(e, field)}
      >
        {label}
      </button>
    ),
    [getSortClass, sortTransfers]
  );

  const getTransfers = (event) => {
    if (event) event.preventDefault();

    showSpinner();

    const queryParams = {
      limit: pageSize,
      search,
      transfer: transferTypeFilter !== "all" ? transferTypeFilter : undefined,
      sort,
      page: page,
    };

    getretrieveTransfers(queryParams, getTransfers_done, jwtToken);
  };

  // API handlers
  const getTransfers_done = (resStatus, responseString, resJson) => {
    closeSpinner();

    switch (resStatus) {
      case 401:
        modal(sessionExpired);
        toScreen(false, navigate, "/logout");
        break;
      case "400":
        setTransfers([]);
        alert(responseString);
        break;
      case 200:
        setTotalPages(Math.ceil(resJson?.totalRecords / pageSize));
        setTransfers(resJson?.data || []);
        break;
      default:
        break;
    }
  };

  const getTransfersDetails = (event, jobId) => {
    if (event) event.preventDefault();

    showSpinner();

    const queryParams = {
      jobId: jobId,
    };

    getretrieveTransfersDetails(queryParams, getTransfersDetails_done, jwtToken);
  };

  // API handlers
  const getTransfersDetails_done = (resStatus, responseString, resJson) => {
    closeSpinner();

    switch (resStatus) {
      case 401:
        modal(sessionExpired);
        toScreen(false, navigate, "/logout");
        break;
      case "400":
        setTransferDetails([]);
        alert(responseString);
        break;
      case 200:
        setTransferDetails(resJson?.data || []);
        break;
      default:
        break;
    }
  };

  const handleInputChange = useCallback(
    (event) => {
      const { name, value, type, checked } = event.target;
      const inputValue = type === "checkbox" ? checked : value;

      switch (name) {
        case "search":
          setSearchValue(inputValue);
          break;
        case "transfer":
          setTransferTypeFilter(inputValue);
          setPage(1);
          break;
        default:
          break;
      }
    },
    [setSearchValue, setTransferTypeFilter, setPage]
  );

  const searchTransfer = () => {
    setSearch(searchValue);
    setTransferTypeFilter("all");
    setPage(1);
  };

  // Memoized components
  const transfersPropsPage = useMemo(
    () => (anchor) =>
    (
      <Box
        sx={getSafariCompatibleBoxSx()}
        role="presentation"
        onClick={(event) => event.stopPropagation()}
        onKeyDown={(event) => event.stopPropagation()}
      >
        <TransfersDetails
          view={currentView}
          onClose={toggleDrawer("right", false)}
          transferDetails={transferDetails}
          userDetails={userDetails}
          theCallBackFunc={getTransfers}
        />
      </Box>
    ),
    [currentView, transferDetails?.jobDetails?._id, userDetails, getTransfers, toggleDrawer]
  );

  // Effects
  useEffect(() => {
    setInputValue(page);
  }, [page, setInputValue]);

  useEffect(() => {
    getTransfers();
  }, [page, pageSize, sort, search, transferTypeFilter]);

  return (
    <div className="l-base-layout__content">
      <h1>Admin / Transfers</h1>
      <div className="c-hr"></div>

      <div className="c-subheader-info-panel">
        <div className="c-subheader-info-panel__search">
          <form
            onSubmit={(e) => {
              e.preventDefault();
              searchTransfer();
            }}
          >
            <input
              type="text"
              value={searchValue || ""}
              onChange={handleInputChange}
              name="search"
              placeholder="Search Transfers..."
              className="c-subheader-info-panel__input"
            />
            <button
              type="button"
              className="c-btn-outline c-btn-outline--white c-btn-outline--small"
              onClick={() => searchValue !== null && searchTransfer()}
            >
              Search
            </button>
          </form>
        </div>
      </div>

      <div className="c-hr"></div>

      <div className="l-row">
        <div className="l-col-100 u-padding-top-0 u-padding-bottom-0">
          <div className="filter-bar">
            {[
              // { id: "preactivation", label: "Pre Activation", count: filterCount?.preactiveUsers },
              {
                id: "all",
                label: "All",
              },
              {
                id: "pending",
                label: "Pending",
              },
              {
                id: "complete",
                label: "Completed",
              },
              {
                id: "rejected",
                label: "Rejected",
              },
              {
                id: "not applicable",
                label: "Not Applicable",
              },
            ]?.map(({ id, label }) => (
              <div className="c-checkbox-radio-block" key={id}>
                <input
                  type="radio"
                  id={id}
                  name="transfer"
                  value={id}
                  onChange={handleInputChange}
                  checked={transferTypeFilter === id}
                />
                <label htmlFor={id}>{label}</label>
              </div>
            ))}
          </div>
        </div>

        <div className="l-col-100">
          <div className="c-hr u-margin-bottom-0"></div>

          <ColumnHeaders />

          <div>
            {transfers?.length > 0 ? (
              transfers?.map(renderTransferRow)
            ) : (
              <div className="u-text-align-center u-margin-top-bottom-m u-color-red">
                No records found
              </div>
            )}
          </div>

          <div className="c-hr u-margin-bottom-0 u-clear-both"></div>

          <Pagination
            pageSize={pageSize}
            totalPages={totalPages}
            page={page}
            setPage={setPage}
            inputValue={inputValue}
            setInputValue={setInputValue}
            setPageSize={setPageSize}
          />
        </div>
      </div>
    </div>
  );
};

Transfers.propTypes = {
  userDetails: PropTypes.object.isRequired,
};

export default Transfers;
